import {
  CREATOR_SITE_SECTION_TYPES,
  type CreatorSiteSectionType,
  type CreatorSiteStatus,
  canTransitionCreatorSiteStatus,
  isCreatorSiteSectionType,
  isCreatorSiteStatus,
  validateCreatorSitePageSlug,
  validateCreatorSiteThemePreset,
  validateCreatorSiteThemeTokens,
  type CreatorSiteThemeTokens,
} from '../creator-presence/creator-site';
import { validateCreatorSiteHeroPrimaryCta } from '../creator-presence/creator-site-sections';
import { DomainError } from '../errors/domain-error';

export function assertCreatorSiteStatusTransition(current: CreatorSiteStatus, next: CreatorSiteStatus) {
  if (!isCreatorSiteStatus(next)) {
    throw new DomainError('Invalid site status');
  }
  if (!canTransitionCreatorSiteStatus(current, next)) {
    throw new DomainError('Invalid site status transition');
  }
}

export function assertCreatorSitePageSlug(slug: string, options?: { isHomepage?: boolean }) {
  const result = validateCreatorSitePageSlug(slug, options);
  if (!result.ok) {
    throw new DomainError(result.message);
  }
  return result.slug;
}

export function assertCreatorSiteSectionType(type: string): CreatorSiteSectionType {
  if (!isCreatorSiteSectionType(type)) {
    throw new DomainError('Invalid section type');
  }
  return type;
}

export function assertCreatorSiteThemePreset(preset: string) {
  const result = validateCreatorSiteThemePreset(preset);
  if (!result.ok) {
    throw new DomainError(result.message);
  }
  return result.preset;
}

export function assertCreatorSiteThemeTokens(tokens: CreatorSiteThemeTokens | null | undefined) {
  const result = validateCreatorSiteThemeTokens(tokens);
  if (!result.ok) {
    throw new DomainError(result.message);
  }
  return result.tokens;
}

export function assertCreatorSitePublishable(input: {
  status: CreatorSiteStatus;
  sections: Array<{ type: string; visible?: boolean; content?: unknown }>;
}) {
  assertCreatorSiteStatusTransition(input.status, 'published');

  const visibleSections = input.sections.filter((section) => section.visible !== false);
  const visibleTypes = visibleSections.map((section) => section.type);

  if (!visibleTypes.includes('hero')) {
    throw new DomainError('A visible hero section is required to publish');
  }

  const hero = visibleSections.find((section) => section.type === 'hero');
  const ctaResult = validateCreatorSiteHeroPrimaryCta(hero?.content);
  if (!ctaResult.ok) {
    throw new DomainError(ctaResult.message);
  }

  for (const type of visibleTypes) {
    if (!(CREATOR_SITE_SECTION_TYPES as readonly string[]).includes(type)) {
      throw new DomainError('Invalid section type');
    }
  }
}
